import { LEVELS, type Level } from '@/domain/constants';
import { COUNTERPART_RANK, shortlistOf } from '@/domain/counterparts';
import { LANE_ATTRIBUTE_VALUES, laneValueLabelOf } from '@/domain/lane-attributes';
import { foldTag } from '@/domain/item-tags';
import {
  isBooleanField,
  isEmptyField,
  isNegated,
  isSetField,
  JUDGMENTS,
  SELF_FIELDS,
  SELF_VALUE,
  type Criterion,
  type EmptyField,
  type Judgment,
} from '@/domain/search-criteria';
import { m, type Message, type PlainKey, type Translator } from '@/i18n';
import { unknownActorName } from '@/lib/directory';
import { fuzzyRank } from '@/lib/fuzzy';

/**
 * The filter bar's suggestion logic (docs/MAP_VIEW.md §3, "search-as-filter"):
 * typing in the big bar suggests **attributes** (filter fields) and **values
 * inside attributes**; picking one either narrows the bar to that attribute
 * (value mode) or immediately becomes a criterion chip. Pure - the fuzzy ranking
 * is `fuzzyRank` (the same engine as the entity pickers, deliberately shared,
 * docs/SEARCH.md tier 1) - so the whole grammar is unit-testable without React.
 *
 * The words come from the catalog (docs/I18N.md): the label tables below hold
 * keys, a chip's label is a `Message` the surface formats, and a suggestion's
 * label is text in the caller's locale, formatted with the translator the
 * caller passes, because the input is ranked against exactly that text.
 */

export interface SuggestContext {
  /** Groups the viewer can name (id + name), the `group` value catalog. */
  groups: { id: string; name: string }[];
  /** People for the `assignee` and `party` catalogs (ref + display name). */
  actors: { ref: string; name: string }[];
  /** Parents for the `parent` catalog (item id + title): the VISIBLE parents
   *  of the surface's gated rows BEFORE its filter (`parentCandidates`), so a
   *  candidate always ships with its name and the set is what the rows show. */
  parents: { id: string; title: string }[];
  /** User tags for the `tag` catalog (the spelling offered + its fold): the
   *  tags of the surface's gated rows BEFORE its filter (`tagCatalog`), so the
   *  catalog stays inside what the viewer can already see
   *  (docs/FIELDS_AND_TAGS.md §6.2). */
  tags: { tag: string; fold: string }[];
  /** The viewer's own ref (the page resolved it on the server). The value
   *  `self` reads as this person's name from `actors`, never as "me": it is
   *  the first value of the two seat holders' lists, and the viewer's literal
   *  ref is that same row (docs/SEARCH.md "The bar"). */
  selfRef: string;
  /** The refs ranked for the viewer, best first (docs/DESIGN_SYSTEM.md "The
   *  person shortlist"): the order of the two seat holders' value lists, and
   *  their shortlist while nothing is typed. The bar and the palette fill it
   *  from `useCounterparts()`; absent, the lists keep the name order. */
  ranked?: readonly string[];
}

/** The `parent` catalog from the rows a surface holds: every visible parent
 *  those rows name (`ListRow.parents` / `WorkItemListing.parents`), once each,
 *  by title. Offering only what the rows show keeps the catalog inside the
 *  viewer's own reach - a parent nobody's row names is not offered. The rows
 *  are the surface's gated listing BEFORE its filter narrowed it (a list's
 *  `unfiltered`, a lens's `catalog`): a catalog built after it could only offer what its own
 *  chips left, and "#bug or #incident" would be unreachable. Pure. */
export function parentCandidates(
  rows: readonly { parents: readonly { id: string; title: string }[] }[],
): { id: string; title: string }[] {
  const seen = new Map<string, string>();
  for (const row of rows) for (const p of row.parents) if (!seen.has(p.id)) seen.set(p.id, p.title);
  return [...seen]
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
}

/** The `tag` catalog from the rows a surface holds (the `parentCandidates`
 *  precedent, the same unfiltered rows): every user tag those rows carry, once
 *  per fold, spelled as most of them spell it, ordered by path. Pure. */
export function tagCatalog(
  rows: readonly { itemTags: readonly string[] }[],
): { tag: string; fold: string }[] {
  const counts = new Map<string, number>();
  for (const row of rows)
    for (const tag of row.itemTags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  return tagCatalogOf([...counts].map(([tag, count]) => ({ tag, count })));
}

/** The same catalog from each spelling and how many rows carry it, as the
 *  lens pages read it over the viewer's whole set (`listingCatalog`,
 *  src/read/board.ts) without loading the rows. Pure. */
export function tagCatalogOf(
  spellings: readonly { tag: string; count: number }[],
): { tag: string; fold: string }[] {
  const byFold = new Map<string, Map<string, number>>();
  for (const { tag, count } of spellings) {
    const fold = foldTag(tag);
    const byTag = byFold.get(fold) ?? byFold.set(fold, new Map()).get(fold)!;
    byTag.set(tag, (byTag.get(tag) ?? 0) + count);
  }
  return [...byFold]
    .map(([fold, byTag]) => ({
      fold,
      tag: [...byTag].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]![0],
    }))
    .sort((a, b) => a.fold.localeCompare(b.fold));
}

export type Suggestion =
  | {
      kind: 'attribute';
      field: Criterion['field'];
      /** e.g. "Standing" - selecting narrows the bar to this attribute's values;
       *  in the caller's locale. */
      label: string;
    }
  | {
      kind: 'criterion';
      criterion: Criterion;
      /** The value line, e.g. "In progress" or "Ari", in the caller's locale. */
      label: string;
      /** The attribute the value belongs to, shown as the row's context. */
      attribute: string;
      /** The row's words as a descriptor, where the bar styles a part of them
       *  (the accent "not" of a negated row); `label` is the same text, plain. */
      display?: Message;
    };

/** Each attribute's name, as a catalog key. */
export const ATTRIBUTE_LABEL: Record<Criterion['field'], PlainKey> = {
  active: 'criteria.attribute.active',
  closed: 'criteria.attribute.closed',
  level: 'criteria.attribute.level',
  assignee: 'criteria.attribute.assignee',
  party: 'criteria.attribute.party',
  group: 'criteria.attribute.group',
  parent: 'criteria.attribute.parent',
  // The parent attribute's "contains" (docs/SEARCH.md "Criteria operators"):
  // one attribute in the bar, two criteria in the grammar.
  ancestor: 'criteria.attribute.parent',
  title: 'criteria.attribute.title',
  tag: 'criteria.attribute.tag',
  // The flow stage editor's words for the same three criteria (its
  // requirements table, `src/app/flows/[flowId]/stage-editor.tsx`).
  tendered: 'criteria.attribute.tendered',
  judgment: 'criteria.attribute.judgment',
  cancelled: 'criteria.attribute.cancelled',
  done: 'criteria.attribute.done',
};

/** The criteria the bar reaches through another attribute's chip menu, never
 *  as an attribute row of their own: `ancestor` is the parent's "contains". */
const MENU_ONLY_FIELDS: readonly Criterion['field'][] = ['ancestor', 'done'];

/**
 * The CONCLUSION's three values (Leon, 2026-11-03: Open / Done / Cancelled),
 * as the `closed` attribute offers them: three criteria over the atoms, so
 * each ending is one filter. `closed: true` alone stays a legal criterion (a
 * saved view, a URL) and reads "Done or cancelled".
 */
const CONCLUSION_VALUES: readonly { criterion: Criterion; label: PlainKey }[] = [
  { criterion: { field: 'closed', value: false }, label: 'attributes.value.closed.no' },
  { criterion: { field: 'done', value: true }, label: 'attributes.value.closed.yes' },
  { criterion: { field: 'cancelled', value: true }, label: 'attributes.value.closed.cancelled' },
];

/** Whether a criterion is the conclusion value at `i` of `CONCLUSION_VALUES`. */
function isConclusionValue(c: Criterion, i: number): boolean {
  const v = CONCLUSION_VALUES[i]!.criterion;
  return c.field === v.field && 'value' in c && 'value' in v && c.value === v.value;
}

/** The value words of the two boolean criteria the stage editor speaks with
 *  yes/no (`tendered`, `cancelled`), and of the judgment (the stage editor's
 *  "none yet", and the verdict's own Accepted / Denied), as catalog keys. */
const YES_NO: readonly { value: boolean; label: PlainKey }[] = [
  { value: true, label: 'criteria.value.yes' },
  { value: false, label: 'criteria.value.no' },
];
export const JUDGMENT_LABEL: Record<Judgment, PlainKey> = {
  none: 'criteria.value.noneYet',
  accepted: 'criteria.value.accepted',
  disputed: 'criteria.value.denied',
};
/** A health grade's name, as the level filter shows it. */
export const LEVEL_LABEL: Record<Level, PlainKey> = {
  info: 'criteria.level.info',
  notice: 'criteria.level.notice',
  warning: 'criteria.level.warning',
  critical: 'criteria.level.critical',
};

/** The value word of a boolean atom criterion (the ONE label source:
 *  domain/lane-attributes.ts - Idle/Active, Open/Closed). */
function atomValueLabel(field: 'active', value: boolean): Message {
  return laneValueLabelOf(field, {
    active: value,
    closed: value,
    delivered: false,
    verdict: 'open',
  });
}

/** Every concrete value of one attribute, as ready-to-add criterion suggestions. */
function valuesOf(field: Criterion['field'], ctx: SuggestContext, t: Translator): Suggestion[] {
  switch (field) {
    case 'active':
      return LANE_ATTRIBUTE_VALUES.active.map((v) => ({
        kind: 'criterion',
        criterion: { field: 'active', value: v.id === 'yes' },
        label: t.msg(v.label),
        attribute: t(ATTRIBUTE_LABEL.active),
      }));
    case 'closed':
    case 'done':
      return CONCLUSION_VALUES.map((v) => ({
        kind: 'criterion',
        criterion: v.criterion,
        label: t(v.label),
        attribute: t(ATTRIBUTE_LABEL.closed),
      }));
    case 'level':
      return LEVELS.map((l) => ({
        kind: 'criterion',
        criterion: { field: 'level', value: l },
        label: t('criteria.value.levelAtLeast', { level: m(LEVEL_LABEL[l]) }),
        attribute: t(ATTRIBUTE_LABEL.level),
      }));
    case 'assignee':
    case 'party':
      // The viewer's own row first, as `self`; their literal ref is that row,
      // so it is not offered a second time.
      return [
        selfRow(field, ctx, t),
        emptyRow(field, t),
        ...rankedOtherActors(ctx).map(
          (a): Suggestion => ({
            kind: 'criterion',
            criterion: { field, values: [a.ref] },
            label: a.name,
            attribute: t(ATTRIBUTE_LABEL[field]),
          }),
        ),
      ];
    case 'group':
      return [
        emptyRow('group', t),
        ...ctx.groups.map(
          (g): Suggestion => ({
            kind: 'criterion',
            criterion: { field: 'group', values: [g.id] },
            label: g.name,
            attribute: t(ATTRIBUTE_LABEL.group),
          }),
        ),
      ];
    case 'parent':
      return [
        emptyRow('parent', t),
        ...ctx.parents.map(
          (p): Suggestion => ({
            kind: 'criterion',
            criterion: { field: 'parent', values: [p.id] },
            label: p.title,
            attribute: t(ATTRIBUTE_LABEL.parent),
          }),
        ),
      ];
    case 'tag':
      return [
        emptyRow('tag', t),
        ...ctx.tags.map(
          (tag): Suggestion => ({
            kind: 'criterion',
            criterion: { field: 'tag', values: [tag.fold] },
            label: t('criteria.value.tag', { tag: tag.tag }),
            attribute: t(ATTRIBUTE_LABEL.tag),
          }),
        ),
      ];
    case 'tendered':
    case 'cancelled':
      return YES_NO.map((v) => ({
        kind: 'criterion',
        criterion: { field, value: v.value },
        label: t(v.label),
        attribute: t(ATTRIBUTE_LABEL[field]),
      }));
    case 'judgment':
      return JUDGMENTS.map((j) => ({
        kind: 'criterion',
        criterion: { field: 'judgment', values: [j] },
        label: t(JUDGMENT_LABEL[j]),
        attribute: t(ATTRIBUTE_LABEL.judgment),
      }));
    case 'title':
    case 'ancestor':
      return [];
  }
}

/** The empty value of a set-valued attribute ("No tag", "Unclaimed"): the
 *  first row of its value list (docs/SEARCH.md "Criteria operators"). */
const EMPTY_LABEL: Record<EmptyField, PlainKey> = {
  assignee: 'criteria.value.unclaimed',
  party: 'criteria.value.noParties',
  group: 'criteria.value.noGroup',
  parent: 'criteria.value.noParent',
  tag: 'criteria.value.noTag',
};

/** The negated empty criterion's chip ("Has a tag", "Has a contributor"). */
const HAS_ONE_LABEL: Record<EmptyField, PlainKey> = {
  assignee: 'criteria.chip.hasContributor',
  party: 'criteria.chip.hasParties',
  group: 'criteria.chip.inAGroup',
  parent: 'criteria.chip.hasParent',
  tag: 'criteria.chip.hasTag',
};

/** The viewer's own name, the words of the value `self`: their name in the
 *  directory, else the placeholder for their ref (`unknownActorName`), never
 *  the ref. */
export function ownName(ctx: SuggestContext): Message | string {
  return ctx.actors.find((a) => a.ref === ctx.selfRef)?.name ?? unknownActorName(ctx.selfRef);
}

/** The people of the two seat holders' lists other than the viewer, whose
 *  own row is `self`. */
export function otherActors(ctx: SuggestContext): SuggestContext['actors'] {
  return ctx.actors.filter((a) => a.ref !== ctx.selfRef);
}

/** The same people, the ranked ones first in their rank (`ctx.ranked`), the
 *  rest after them in name order. */
export function rankedOtherActors(ctx: SuggestContext): SuggestContext['actors'] {
  return shortlistOf(
    otherActors(ctx),
    (a) => a.ref,
    { self: null, ranked: ctx.ranked ?? [] },
    { size: Number.POSITIVE_INFINITY },
  );
}

/** How many rows a seat holder's value list shows while nothing is typed:
 *  the viewer's own row, the empty value, and the person shortlist. */
const SEAT_HOLDER_SHORTLIST = 2 + COUNTERPART_RANK.shortlist;

/** The person looking, the first value of the two seat holders' lists
 *  (docs/SEARCH.md "Parties"), under their own name: the grammar's `self`,
 *  which each opener evaluates as their own ref and every group they act
 *  through. */
function selfRow(field: 'assignee' | 'party', ctx: SuggestContext, t: Translator): Suggestion {
  const own = ownName(ctx);
  return {
    kind: 'criterion',
    criterion: { field, values: [SELF_VALUE] },
    label: typeof own === 'string' ? own : t.msg(own),
    attribute: t(ATTRIBUTE_LABEL[field]),
  };
}

/** A seat holder's value list as the viewer reads it: `self` and the viewer's
 *  literal ref are one person, so the second of them is left out. */
export function collapsedHolders(values: readonly string[], selfRef: string): string[] {
  const out: string[] = [];
  for (const v of values) {
    const own = v === SELF_VALUE || v === selfRef;
    if (own ? out.some((o) => o === SELF_VALUE || o === selfRef) : out.includes(v)) continue;
    out.push(v);
  }
  return out;
}

/** A seat holder's name in a chip: `self` as the viewer's own name, a ref by
 *  the directory's name, else the placeholder every surface names such a ref
 *  with (`unknownActorName`: "Unknown user", "Unknown group", an
 *  automation's slug), never the ref. */
export function holderName(v: string, ctx: SuggestContext): Message | string {
  if (v === SELF_VALUE) return ownName(ctx);
  return ctx.actors.find((a) => a.ref === v)?.name ?? unknownActorName(v);
}

function emptyRow(field: EmptyField, t: Translator): Suggestion {
  return {
    kind: 'criterion',
    criterion: { field, empty: true },
    label: t(EMPTY_LABEL[field]),
    attribute: t(ATTRIBUTE_LABEL[field]),
  };
}

const MAX_VALUES_PER_ATTRIBUTE = 5;
const MAX_TOTAL = 12;

/** The open-mode prefix that negates every value row: a hyphen, or the minus
 *  sign the bar's hint prints (docs/SEARCH.md "Criteria operators"). */
const NEGATE_PREFIX = /^[-−]/;

/** Does this open-mode text start with the negating minus? Its text is then a
 *  value to exclude, not a title: the bar offers no item hits for it. */
export function isNegatedQuery(input: string): boolean {
  return NEGATE_PREFIX.test(input.trim());
}

/** The attributes whose values open mode matches directly. The judgment's
 *  values are words worth matching; the yes/no of `tendered` and `cancelled`
 *  are reached through their attribute. */
const VALUE_FIELDS = [
  'active',
  'closed',
  'level',
  'assignee',
  'party',
  'group',
  'parent',
  'tag',
  'judgment',
] as const;

/** The title fallback row: "contains “x”", or negated "not containing “x”". */
function titleRow(q: string, not: boolean, t: Translator): Suggestion {
  if (!not) {
    return {
      kind: 'criterion',
      criterion: { field: 'title', value: q },
      label: t('criteria.value.titleContains', { text: q }),
      attribute: t(ATTRIBUTE_LABEL.title),
    };
  }
  const display = m('criteria.value.titleNotContains', { not: m('criteria.word.not'), text: q });
  return {
    kind: 'criterion',
    criterion: { field: 'title', value: q, not: true },
    label: t.msg(display),
    attribute: t(ATTRIBUTE_LABEL.title),
    display,
  };
}

/** A value row read negated: its criterion negated, and its words the
 *  negated reading ("not #legacy", "< warning", "Has a tag"). */
function negatedRow(s: Suggestion, t: Translator): Suggestion {
  if (s.kind !== 'criterion') return s;
  const c = s.criterion;
  const display: Message =
    'empty' in c && c.empty && isEmptyField(c.field)
      ? m(HAS_ONE_LABEL[c.field])
      : c.field === 'level'
        ? m('criteria.value.levelBelow', { level: m(LEVEL_LABEL[c.value]) })
        : m('criteria.chip.not', { value: s.label });
  return { ...s, criterion: negated(c), label: t.msg(display), display };
}

/** Open mode under a leading minus: the value rows of every attribute that
 *  can be negated, each read negated, and the negated title row last. No
 *  attribute rows, and nothing until a character follows the minus. */
function negatedSuggestions(
  q: string,
  ctx: SuggestContext,
  t: Translator,
  omit: readonly Criterion['field'][],
): Suggestion[] {
  if (!q) return [];
  const out: Suggestion[] = [];
  for (const field of VALUE_FIELDS) {
    if (omit.includes(field) || isBooleanField(field)) continue;
    const hits = fuzzyRank(q, valuesOf(field, ctx, t), (s) => s.label)
      .map((r) => negatedRow(r.item, t))
      .slice(0, MAX_VALUES_PER_ATTRIBUTE);
    out.push(...hits);
  }
  out.push(titleRow(q, true, t));
  return out.slice(0, MAX_TOTAL);
}

/**
 * The suggestion list for the bar's current state. Two modes:
 *
 * - **Value mode** (`pendingField` set): only that attribute's values, fuzzy-
 *   narrowed by the input; an empty input lists them all, the empty value
 *   ("No tag") first, after the viewer's own name on Contributor and
 *   Parties, where the people that follow are the person shortlist
 *   (`ctx.ranked`, six of them) until something is typed. `title` pending
 *   offers the one "contains" row. The text is literal here, so a title that
 *   starts with "-" is reached through Title.
 * - **Open mode**: an empty input lists the attributes; text fuzzy-matches
 *   attributes (to narrow) AND values across every attribute (to add directly),
 *   with a "Title contains …" fallback last - so free text always does something.
 *   A leading `-` reads every value row negated ("not #legacy") and offers no
 *   attribute rows; the fallback becomes "not containing …".
 *
 * `omit` names attributes the caller does not offer (the custom groups editor
 * leaves out Group, its `OMIT_FIELDS`): neither they nor their values are
 * offered.
 *
 * `t` is the caller's translator: the labels are ranked and shown in its
 * locale.
 */
export function suggestFilters(
  input: string,
  pendingField: Criterion['field'] | null,
  ctx: SuggestContext,
  t: Translator,
  omit: readonly Criterion['field'][] = [],
): Suggestion[] {
  const q = input.trim();

  if (pendingField === 'title') return q ? [titleRow(q, false, t)] : [];
  if (pendingField) {
    const all = valuesOf(pendingField, ctx, t);
    if (!q) {
      const seatHolder = (SELF_FIELDS as readonly string[]).includes(pendingField);
      return all.slice(0, seatHolder ? SEAT_HOLDER_SHORTLIST : MAX_TOTAL);
    }
    return fuzzyRank(q, all, (s) => s.label)
      .map((r) => r.item)
      .slice(0, MAX_TOTAL);
  }
  if (isNegatedQuery(q)) return negatedSuggestions(q.slice(1).trim(), ctx, t, omit);

  const attributes: Suggestion[] = (Object.keys(ATTRIBUTE_LABEL) as Criterion['field'][])
    .filter((field) => !omit.includes(field) && !MENU_ONLY_FIELDS.includes(field))
    .map((field) => ({ kind: 'attribute', field, label: t(ATTRIBUTE_LABEL[field]) }));
  if (!q) return attributes;

  const out: Suggestion[] = fuzzyRank(q, attributes, (s) => s.label).map((r) => r.item);
  for (const field of VALUE_FIELDS) {
    if (omit.includes(field)) continue;
    const hits = fuzzyRank(q, valuesOf(field, ctx, t), (s) => s.label)
      .map((r) => r.item)
      .slice(0, MAX_VALUES_PER_ATTRIBUTE);
    out.push(...hits);
  }
  out.push(titleRow(q, false, t));
  return out.slice(0, MAX_TOTAL);
}

// --- The operators in the bar (docs/SEARCH.md "Criteria operators") ---

/** The criterion with its `not` set or cleared, keys in the grammar's
 *  canonical order (field, values / empty / value, not). A boolean is
 *  returned as it is: it is never negated. */
export function withNegation(c: Criterion, not: boolean): Criterion {
  if (isBooleanField(c.field)) return c;
  const rest: Record<string, unknown> = { ...c };
  delete rest.not;
  return (not ? { ...rest, not: true } : rest) as Criterion;
}

/** The criterion negated, as Shift+Enter adds it: a boolean's negation is
 *  its other value, every other criterion takes `not`. */
export function negated(c: Criterion): Criterion {
  switch (c.field) {
    case 'closed':
    case 'active':
    case 'tendered':
    case 'cancelled':
    case 'done':
      return { field: c.field, value: !c.value };
    default:
      return withNegation(c, true);
  }
}

/** The one value a positive set-valued row carries, or null (the empty row,
 *  a negated row, any other attribute). */
function rowValue(row: Criterion): string | null {
  if (!isSetField(row.field) || isNegated(row)) return null;
  return 'values' in row && row.values && row.values.length === 1 ? row.values[0]! : null;
}

/**
 * The chip an "or" joins this row's value to (Alt+Enter, the Or pill): the
 * LAST positive chip of the same set-valued attribute that holds values and
 * not this one. -1 when there is none: an empty chip cannot take a value (an
 * item without tags carries none of them), and a negated chip is "none of".
 * On a seat holder, `self` and the viewer's literal ref (`selfRef`) are one
 * value: a chip holding either already holds the viewer.
 */
export function joinTarget(
  criteria: readonly Criterion[],
  row: Criterion,
  selfRef?: string,
): number {
  const value = rowValue(row);
  if (value === null) return -1;
  const own = (v: string) => v === SELF_VALUE || (selfRef !== undefined && v === selfRef);
  const holds = (values: readonly string[]) =>
    (SELF_FIELDS as readonly string[]).includes(row.field) && own(value)
      ? values.some(own)
      : values.includes(value);
  for (let i = criteria.length - 1; i >= 0; i--) {
    const c = criteria[i]!;
    if (c.field !== row.field || isNegated(c)) continue;
    if (!('values' in c) || !c.values) continue;
    return holds(c.values as readonly string[]) ? -1 : i;
  }
  return -1;
}

/** The chip with the row's value joined to its values (the "or"). */
export function joinValue(chip: Criterion, row: Criterion): Criterion {
  const value = rowValue(row);
  if (value === null || !('values' in chip) || !chip.values) return chip;
  const values = [...(chip.values as readonly string[]), value];
  return withNegation({ field: chip.field, values } as Criterion, isNegated(chip));
}

/** One reading a chip's menu offers, as a value row: its words, the
 *  criterion it makes the chip, and whether the chip reads that way now. */
export interface ChipChoice {
  label: Message;
  criterion: Criterion;
  selected: boolean;
}

/** The chips whose value IS their state, so their menu of states is their
 *  editor: a boolean, and a set-valued attribute asked whether it holds
 *  nothing ("No tag" / "Has a tag"). Every other attribute chip opens the
 *  chip editor (`chip-editor-model.ts`); a title chip opens its text. */
export type MenuCriterion = Extract<Criterion, { value: boolean } | { empty: true }>;

export function isMenuCriterion(c: Criterion): c is MenuCriterion {
  return isBooleanField(c.field) || ('empty' in c && c.empty === true);
}

/**
 * What a menu chip's menu offers (docs/SEARCH.md "The bar"): its two
 * readings, the current one selected, and nothing else (a menu of states; the
 * chip's ✕ removes it). A boolean offers its two values, an empty chip its two
 * readings ("No tag" / "Has a tag").
 */
export function chipChoices(c: MenuCriterion): ChipChoice[] {
  switch (c.field) {
    case 'active':
      return LANE_ATTRIBUTE_VALUES.active.map((v) => ({
        label: v.label,
        criterion: { field: 'active', value: v.id === 'yes' },
        selected: c.value === (v.id === 'yes'),
      }));
    case 'closed':
    case 'done':
      return CONCLUSION_VALUES.map((v, i) => ({
        label: m(v.label),
        criterion: v.criterion,
        selected: isConclusionValue(c, i),
      }));
    case 'tendered':
    case 'cancelled': {
      const field = c.field;
      return YES_NO.map((v) => ({
        label: m(v.label),
        criterion: { field, value: v.value },
        selected: c.value === v.value,
      }));
    }
    default: {
      const not = isNegated(c);
      return [
        { label: m(EMPTY_LABEL[c.field]), criterion: withNegation(c, false), selected: !not },
        { label: m(HAS_ONE_LABEL[c.field]), criterion: withNegation(c, true), selected: not },
      ];
    }
  }
}

/** The chip text for an applied criterion, e.g. "Active", "Open", "“api”",
 *  "#bug or #incident" or "Contributor ≠ Blake", as a descriptor the
 *  surface formats. A chip that names an attribute is one of the attribute
 *  sentences (`criteria.chip.is` / `isNot`, the level's `≥` / `<`), whose
 *  `{attribute}` and `{value}` the chip draws as separate parts; a negated tag
 *  or title, which have no attribute word, leads with `criteria.chip.not`. A
 *  values list joins by "or"; an empty criterion reads as its own words ("No
 *  tag" / "Has a tag"). */
export function criterionChipLabel(c: Criterion, ctx: SuggestContext): Message {
  const not = isNegated(c);
  const named = (field: Criterion['field'], value: Message | string): Message =>
    m(not ? 'criteria.chip.isNot' : 'criteria.chip.is', {
      attribute: m(ATTRIBUTE_LABEL[field]),
      value,
    });
  switch (c.field) {
    case 'active':
      // The value word alone ("Idle", "Active"): the value labels name the
      // primitives, and a prefix would just repeat them. The words are the
      // domain's (lane-attributes.ts).
      return atomValueLabel(c.field, c.value);
    case 'closed':
      // The conclusion: "Open", or both endings at once.
      return c.value ? m('criteria.value.doneOrCancelled') : m('attributes.value.closed.no');
    case 'done':
      return c.value
        ? m('attributes.value.closed.yes')
        : m('criteria.chip.isNot', {
            attribute: m(ATTRIBUTE_LABEL.closed),
            value: m('attributes.value.closed.yes'),
          });
    case 'level':
      return m(not ? 'criteria.chip.levelBelow' : 'criteria.chip.levelAtLeast', {
        attribute: m(ATTRIBUTE_LABEL.level),
        value: m(LEVEL_LABEL[c.value]),
      });
    case 'assignee':
    case 'party':
      if (c.empty) return emptyChip(c.field, not);
      return named(
        c.field,
        orList(collapsedHolders(c.values, ctx.selfRef).map((v) => holderName(v, ctx))),
      );
    case 'group':
      if (c.empty) return emptyChip('group', not);
      return named('group', orList(c.values.map((v) => groupName(v, ctx))));
    case 'parent':
      if (c.empty) return emptyChip('parent', not);
      return named('parent', orList(c.values.map((v) => parentTitle(v, ctx))));
    case 'ancestor':
      // The parent attribute's "contains": any depth, the item itself excluded.
      return m(not ? 'criteria.chip.containsNot' : 'criteria.chip.contains', {
        attribute: m(ATTRIBUTE_LABEL.ancestor),
        value: orList(c.values.map((v) => parentTitle(v, ctx))),
      });
    case 'title': {
      const text = m('criteria.chip.title', { text: c.value });
      return not ? m('criteria.chip.not', { value: text }) : text;
    }
    case 'tag': {
      if (c.empty) return emptyChip('tag', not);
      const tags = orList(c.values.map((v) => tagWords(v, ctx)));
      return not ? m('criteria.chip.not', { value: tags }) : tags;
    }
    case 'tendered':
    case 'cancelled':
      // A boolean is never negated: its other value is the negation.
      return named(c.field, m(c.value ? 'criteria.value.yes' : 'criteria.value.no'));
    case 'judgment':
      return named('judgment', orList(c.values.map((j) => m(JUDGMENT_LABEL[j]))));
  }
}

/** A group's name by its id, else the id. */
export function groupName(id: string, ctx: SuggestContext): string {
  return ctx.groups.find((g) => g.id === id)?.name ?? id;
}

/** A parent's title by its id, else the id (a `parent=` chip from a shared
 *  URL on a surface with no catalog labels itself by id). */
export function parentTitle(id: string, ctx: SuggestContext): string {
  return ctx.parents.find((p) => p.id === id)?.title ?? id;
}

/** A tag value's words ("#area/frontend"): the catalog's spelling of the
 *  fold, else the fold. */
export function tagWords(fold: string, ctx: SuggestContext): Message {
  return m('criteria.value.tag', { tag: ctx.tags.find((t) => t.fold === fold)?.tag ?? fold });
}

/** An empty criterion's chip: the empty value's own words, or, negated, the
 *  "has one" reading. */
function emptyChip(field: EmptyField, not: boolean): Message {
  return m(not ? HAS_ONE_LABEL[field] : EMPTY_LABEL[field]);
}

/** Values joined by "or" ("a or b or c"), as one descriptor (a lone value
 *  stays as it is). A criterion's values list is never empty. */
function orList(values: readonly Message[]): Message;
function orList(values: readonly (string | Message)[]): Message | string;
function orList(values: readonly (string | Message)[]): Message | string {
  if (values.length === 0) return '';
  return values
    .slice(0, -1)
    .reduceRight<
      Message | string
    >((second, first) => m('criteria.chip.or', { first, second }), values[values.length - 1]!);
}
